/**
 * Desktop Left Rail Navigation (AG-008 / NAV-04).
 * Single source of truth: FIRM_PRIMARY_NAV.
 * 5 core slots + More: Home · Sell · Stock · Make · Money · More.
 * Collapsible to icons-only (64px <-> 224px).
 */
import { useState, useEffect } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { getDesktopRailSlots } from "@/lib/firm-primary-nav";
import { getShortcutKeysForSlot } from "@/lib/shortcuts-store";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STORAGE_KEY = "avs_desktop_rail_collapsed";

export function DesktopSideRail() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const slots = getDesktopRailSlots();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // ignore local storage error
    }
  }, [collapsed]);

  return (
    <aside
      data-testid="desktop-side-rail"
      className={cn(
        "hidden xl:flex flex-col shrink-0 border-r border-border bg-card/90 backdrop-blur-md transition-all duration-200 ease-in-out no-print z-30 select-none",
        collapsed ? "w-16" : "w-52",
      )}
      aria-label="Desktop primary navigation"
    >
      {/* Rail Nav Items */}
      <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto scrollbar-none" aria-label="Modules">
        {slots.map((slot) => {
          const Icon = slot.icon;
          const active = slot.match(pathname);
          const shortcut = getShortcutKeysForSlot(slot.id);

          return (
            <Link
              key={slot.id}
              to={slot.to as never}
              search={(slot.search ?? undefined) as never}
              title={collapsed ? `${slot.word}${shortcut ? ` (${shortcut})` : ""}` : undefined}
              className={cn(
                "flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-colors cursor-pointer group relative",
                active
                  ? "bg-gold/15 text-gold-deep dark:text-gold font-semibold shadow-xs"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                collapsed && "justify-center px-0",
              )}
              aria-current={active ? "page" : undefined}
            >
              {/* Active left indicator bar */}
              {active ? (
                <span
                  className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-gold"
                  aria-hidden="true"
                />
              ) : null}

              <span
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors",
                  active
                    ? "bg-gold/20 text-gold-deep dark:text-gold"
                    : "text-muted-foreground group-hover:text-foreground",
                )}
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              </span>

              {!collapsed && (
                <div className="flex flex-1 items-center justify-between min-w-0">
                  <span className="truncate">{slot.word}</span>
                  {shortcut ? (
                    <span className="text-[10px] font-mono text-muted-foreground/70 tracking-tight hidden lg:inline">
                      {shortcut}
                    </span>
                  ) : null}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Collapse / Expand Rail Toggle */}
      <div className="p-2 border-t border-border/80">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className={cn(
            "w-full flex items-center gap-2 rounded-md py-2 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer",
            collapsed && "justify-center px-0",
          )}
          title={collapsed ? "Expand navigation rail" : "Collapse navigation rail"}
          aria-label={collapsed ? "Expand navigation rail" : "Collapse navigation rail"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 shrink-0" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 shrink-0" />
              <span className="truncate">Collapse rail</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
